import React, { useEffect, useState, useMemo } from 'react';
import { Modal, Input, List, Tag, Typography, Space } from 'antd';
import {
  SearchOutlined,
  DashboardOutlined,
  ProjectOutlined,
  DatabaseOutlined,
  SyncOutlined,
  BarChartOutlined,
  BugOutlined,
  DeploymentUnitOutlined,
  SettingOutlined,
  PlayCircleOutlined,
  FileTextOutlined,
  ThunderboltOutlined,
  CloudSyncOutlined,
  
  
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useProjects } from '@/hooks/useProject';
import { useAppStore } from '@/store';
import { api } from '@/api/client';
import { RhinoIcon } from '@/components/common/RhinoIcon';
import type { Project } from '@/types';
import { useT } from '@/i18n';
import { useAccess, type Permission } from '@/auth/useAccess';

const { Text } = Typography;

interface CommandItem {
  id: string;
  title: string;
  subtitle?: string;
  category: 'Navigation' | 'Projects' | 'Migration Runs' | 'Entities' | 'Actions';
  path: string;
  icon: React.ReactNode;
  onSelect?: () => void;
  badge?: string;
  badgeColor?: string;
}

// Titles are English for reading the code; the palette shows them by id in the
// interface language.
const STATIC_COMMAND_ITEMS: CommandItem[] = [
  { id: 'dash', title: 'Dashboard & Overview', category: 'Navigation', path: '/', icon: <DashboardOutlined /> },
  { id: 'proj-all', title: 'All Projects', category: 'Navigation', path: '/projects', icon: <ProjectOutlined /> },
  { id: 'run-sub', title: 'Submit Data Migration Run', category: 'Navigation', path: '/runs/submit', icon: <PlayCircleOutlined /> },
  { id: 'run-sched', title: 'Scheduled Runs & Automated Jobs', category: 'Navigation', path: '/scheduled-runs', icon: <PlayCircleOutlined /> },
  { id: 'run-hist', title: 'Run History & Logs', category: 'Navigation', path: '/runs', icon: <DatabaseOutlined /> },
  { id: 'fusion-export', title: 'Fusion Setup Export', category: 'Navigation', path: '/runs/fusion-export', icon: <FileTextOutlined /> },
  { id: 'reconcil', title: 'Data Reconciliation Engine', category: 'Navigation', path: '/reconciliation', icon: <SyncOutlined /> },
  { id: 'data-sync', title: 'Data Sync Pipelines', category: 'Navigation', path: '/data-sync', icon: <CloudSyncOutlined /> },
  { id: 'reports', title: 'Analytics & Reports', category: 'Navigation', path: '/reports', icon: <BarChartOutlined /> },
  { id: 'rhino', title: 'Rhino SQL Workbench', category: 'Navigation', path: '/rhino', icon: <RhinoIcon /> },
  { id: 'insight', title: 'Insight Decision Intelligence', category: 'Navigation', path: '/insight', icon: <BarChartOutlined /> },
  { id: 'confy', title: 'Confy - Oracle Fusion Configuration', category: 'Navigation', path: '/confy', icon: <SettingOutlined /> },
  { id: 'ent-lib', title: 'Entity Library & Templates', category: 'Navigation', path: '/entity-library', icon: <DeploymentUnitOutlined /> },
  { id: 'credits', title: 'Credits & Balance', category: 'Navigation', path: '/credits', icon: <ThunderboltOutlined /> },
  { id: 'issues', title: 'Issue Tracker', category: 'Navigation', path: '/issues', icon: <BugOutlined /> },
  { id: 'admin-users', title: 'User Management & Roles', category: 'Navigation', path: '/manage/users', icon: <SettingOutlined /> },
  { id: 'admin-cfg', title: 'System Configuration', category: 'Navigation', path: '/manage/config', icon: <SettingOutlined /> },
  { id: 'admin-keys', title: 'Key Store & Credentials', category: 'Navigation', path: '/manage/keystore', icon: <SettingOutlined /> },
  { id: 'admin-analytics', title: 'Usage & Analytics', category: 'Navigation', path: '/manage/analytics', icon: <BarChartOutlined /> },
];

// What opens each page, as in the side menu (MainLayout NEEDS), so the palette
// never offers a page the person would be turned away from.
const ITEM_NEEDS: Record<string, Permission> = {
  'proj-all': 'projects.view',
  'run-sub': 'runs.use',
  'run-sched': 'runs.use',
  'run-hist': 'runs.use',
  'fusion-export': 'runs.use',
  reconcil: 'runs.use',
  'data-sync': 'data.sync',
  reports: 'reports.view',
  rhino: 'rhino.use',
  confy: 'confy.use',
  'ent-lib': 'library.view',
  credits: 'credits.view',
  issues: 'issues.use',
  'admin-users': 'platform.admin',
  'admin-cfg': 'platform.admin',
  'admin-keys': 'platform.admin',
  'admin-analytics': 'platform.admin',
};

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
}

const CATEGORY_KEY: Record<CommandItem['category'], string> = {
  Navigation: 'navigation',
  Projects: 'projects',
  'Migration Runs': 'runs',
  Entities: 'entities',
  Actions: 'actions',
};

export function CommandPalette({ open, onClose }: CommandPaletteProps) {
  const { t } = useT('shell');
  const categoryLabel = (cat: CommandItem['category']) => t(`palette.category.${CATEGORY_KEY[cat]}`, { defaultValue: cat });
  const navigate = useNavigate();
  const { can } = useAccess();
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);

  const { data: projects = [] } = useProjects();
  const setActiveProjectId = useAppStore((s) => s.setActiveProjectId);
  const setCurrentProject = useAppStore((s) => s.setCurrentProject);

  // Fetch recent runs for instant search
  const { data: recentRuns = [] } = useQuery({
    queryKey: ['palette', 'recent-runs'],
    queryFn: () => api.get<any[]>('/reports/recent-runs', { limit: 30 }).catch(() => []),
    enabled: open,
    staleTime: 30_000,
  });

  // Fetch entities for instant search
  const { data: entities = [] } = useQuery({
    queryKey: ['palette', 'entities'],
    queryFn: () =>
      api.get<any>('/entities')
        .then((res: any) => (Array.isArray(res) ? res : res?.items || []))
        .catch(() => []),
    enabled: open,
    staleTime: 60_000,
  });

  useEffect(() => {
    if (open) {
      setQuery('');
      setSelectedIndex(0);
    }
  }, [open]);

  // Combine static commands with dynamic live objects
  const allItems = useMemo<CommandItem[]>(() => {
    const items: CommandItem[] = STATIC_COMMAND_ITEMS.filter((item) => !ITEM_NEEDS[item.id] || can(ITEM_NEEDS[item.id])).map((item) => ({
      ...item,
      title: t(`palette.item.${item.id}`, { defaultValue: item.title }),
    }));

    // Add Projects
    projects.forEach((p: Project) => {
      items.push({
        id: `proj-${p.id}`,
        title: `${p.project_code} - ${p.project_name}`,
        subtitle: `${p.customer_name ?? t('palette.client')} · ${p.source_system}`,
        category: 'Projects',
        path: `/projects/${p.id}`,
        icon: <ProjectOutlined style={{ color: 'var(--dp-violet)' }} />,
        badge: p.status,
        badgeColor: p.status === 'ACTIVE' ? 'green' : 'default',
        onSelect: () => {
          setActiveProjectId(p.id);
          setCurrentProject(p);
        },
      });
    });

    // Add Recent Runs
    recentRuns.forEach((r: any) => {
      const status = r.run_status || 'UNKNOWN';
      const isOk = status.includes('COMPLETED') || status === 'SUCCESS';
      const isErr = status.includes('ERROR') || status.includes('FAIL');
      items.push({
        id: `run-${r.id}`,
        title: t('palette.runTitle', { id: r.id, project: r.project_code || t('palette.project') }),
        subtitle: r.entity_group_name || (r.entity_names?.length ? r.entity_names.join(', ') : t('palette.runDetail')),
        category: 'Migration Runs',
        path: `/projects/${r.project_id}/runs/${r.id}`,
        icon: isOk ? (
          <CheckCircleOutlined style={{ color: 'var(--dp-success)' }} />
        ) : isErr ? (
          <CloseCircleOutlined style={{ color: 'var(--dp-error)' }} />
        ) : (
          <ClockCircleOutlined style={{ color: 'var(--dp-warning)' }} />
        ),
        badge: status,
        badgeColor: isOk ? 'green' : isErr ? 'red' : 'gold',
      });
    });

    // Add Entities
    entities.slice(0, 50).forEach((e: any) => {
      items.push({
        id: `entity-${e.id}`,
        title: `${e.entity_code}`,
        subtitle: e.entity_name || e.description || t('palette.entityDefinition'),
        category: 'Entities',
        path: `/entity-library`,
        icon: <DeploymentUnitOutlined style={{ color: 'var(--dp-teal)' }} />,
        badge: e.load_type || 'ENTITY',
        badgeColor: 'cyan',
      });
    });

    return items;
  }, [projects, recentRuns, entities, setActiveProjectId, setCurrentProject, t, can]);

  const filteredItems = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      // Default view shows primary navigation and projects
      return allItems.filter(
        (i) => i.category === 'Navigation' || i.category === 'Projects',
      );
    }
    return allItems.filter(
      (item) =>
        item.title.toLowerCase().includes(q) ||
        (item.subtitle && item.subtitle.toLowerCase().includes(q)) ||
        item.category.toLowerCase().includes(q) ||
        t(`palette.category.${CATEGORY_KEY[item.category]}`, { defaultValue: item.category }).toLowerCase().includes(q) ||
        item.path.toLowerCase().includes(q) ||
        (item.badge && item.badge.toLowerCase().includes(q)),
    );
  }, [allItems, query, t]);

  const handleSelect = (item: CommandItem) => {
    if (item.onSelect) {
      item.onSelect();
    }
    onClose();
    navigate(item.path);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev + 1) % Math.max(1, filteredItems.length));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev - 1 + filteredItems.length) % Math.max(1, filteredItems.length));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filteredItems[selectedIndex]) {
        handleSelect(filteredItems[selectedIndex]);
      }
    }
  };

  const getCategoryColor = (cat: string) => {
    switch (cat) {
      case 'Projects':
        return 'purple';
      case 'Migration Runs':
        return 'blue';
      case 'Entities':
        return 'cyan';
      case 'Actions':
        return 'orange';
      default:
        return 'default';
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      closable={false}
      width={680}
      style={{ top: 70 }}
      styles={{ body: { padding: 0 } }}
    >
      <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--dp-line-soft)' }}>
        <Input
          autoFocus
          prefix={<SearchOutlined style={{ color: 'var(--dp-violet-2)', fontSize: 18, marginInlineEnd: 8 }} />}
          placeholder={t('palette.placeholder')}
          variant="borderless"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setSelectedIndex(0);
          }}
          onKeyDown={handleKeyDown}
          style={{ fontSize: 15 }}
        />
      </div>

      <div style={{ maxHeight: 420, overflowY: 'auto', padding: '8px 12px' }}>
        {filteredItems.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--dp-ink-faint)' }}>
            {t('palette.noResults', { query })}
          </div>
        ) : (
          <List
            dataSource={filteredItems}
            renderItem={(item, index) => {
              const isSelected = index === selectedIndex;
              return (
                <List.Item
                  onClick={() => handleSelect(item)}
                  style={{
                    padding: '9px 14px',
                    borderRadius: 8,
                    cursor: 'pointer',
                    background: isSelected ? 'var(--dp-violet-wash)' : 'transparent',
                    borderInlineStart: isSelected ? '3px solid var(--dp-violet)' : '3px solid transparent',
                    transition: 'all 0.15s ease',
                    marginBottom: 2,
                    borderBottom: 'none',
                  }}
                  onMouseEnter={() => setSelectedIndex(index)}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 12 }}>
                    <Space size={12} style={{ minWidth: 0, flex: 1 }}>
                      <span style={{ fontSize: 17, flexShrink: 0 }}>{item.icon}</span>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Text
                            strong
                            style={{
                              color: isSelected ? 'var(--dp-violet-soft)' : 'var(--dp-ink)',
                              fontSize: 13,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {item.title}
                          </Text>
                          {item.badge && (
                            <Tag color={item.badgeColor || 'default'} style={{ fontSize: 10, margin: 0, padding: '0 6px', lineHeight: '18px' }}>
                              {item.badge}
                            </Tag>
                          )}
                        </div>
                        {item.subtitle && (
                          <div
                            style={{
                              fontSize: 11.5,
                              color: 'var(--dp-ink-faint)',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {item.subtitle}
                          </div>
                        )}
                      </div>
                    </Space>
                    <Tag color={getCategoryColor(item.category)} style={{ fontSize: 10.5, margin: 0, flexShrink: 0 }}>
                      {categoryLabel(item.category)}
                    </Tag>
                  </div>
                </List.Item>
              );
            }}
          />
        )}
      </div>

      <div
        style={{
          padding: '8px 18px',
          background: 'var(--dp-surface-2)',
          borderTop: '1px solid var(--dp-line-soft)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontSize: 11,
          color: 'var(--dp-ink-dim)',
          borderRadius: '0 0 12px 12px',
        }}
      >
        <span>
          <Text keyboard style={{ fontSize: 10 }}>↑↓</Text> {t('palette.navigate')} · <Text keyboard style={{ fontSize: 10 }}>↵</Text> {t('palette.select')}
        </span>
        <span>
          <Text keyboard style={{ fontSize: 10 }}>ESC</Text> {t('palette.dismiss')}
        </span>
      </div>
    </Modal>
  );
}

export default CommandPalette;
